import { CLASS_NAME } from 'constants/class-names'
import { ATTR_NAME } from 'constants/attr-name'
import {
    AriaAttr,
    EventType,
    HtmlAttr,
    KeyType,
    focusAbleElementsSelector
} from 'constants/enums'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { isMobile } from 'utils/system/device'

enum BackdropStatus {
    OPEN,
    CLOSE
}

export class ModalComponent extends ComponentBase {
    public static activated: Nullable<ModalComponent>
    protected modal: Nullable<HTMLElement>

    private readonly modalDialogPrefix = 'modalDialog'

    private id: string
    private lastYposition: number
    private backdropEnabled = false

    private lastFocusAbleElement: Nullable<HTMLElement>
    private modalTitle: Nullable<HTMLHeadingElement>
    private closeBtn: HTMLButtonElement

    /**
     * TODO This will fix the iOS full height problem.
     * If you need to fix another, please consider moving this to the root class.
     * You can simply put `height: var(--app-height);` to the CSS selector.
     */
    private appHeight = () => {
        const doc = document.documentElement
        doc.style.setProperty('--app-height', `${window.innerHeight}px`)
    }

    /**
     * Get the last element which is focus-able
     * @return Nullable<HTMLElement>
     */
    private get _lastFocusAbleElement(): Nullable<HTMLElement> {
        const focusAbleElements = this.getElements(
            focusAbleElementsSelector,
            this.modal
        )

        if (focusAbleElements.length > 1) {
            return focusAbleElements.slice(-1)[0]
        }

        return undefined
    }

    /**
     * Initializes listening for actions
     * requesting modals to be opened
     */
    public constructor(
        protected readonly modalControl?: HTMLElement,
        id?: string
    ) {
        super()

        this.id =
            id ||
            (modalControl &&
                modalControl.getAttribute(ATTR_NAME.main.modal.modalControl)) ||
            ''
        if (!this.id) {
            return
        }

        const modal = this.getElement(`#${this.modalDialogPrefix}${this.id}`)
        const closeBtn = this.getElement<HTMLButtonElement>(
            `.${CLASS_NAME.modal.closeBtn}`,
            modal
        )
        const title = this.getElement<HTMLHeadingElement>(
            `.${CLASS_NAME.modal.title}`,
            modal
        )

        if (!modal || !title || !closeBtn) {
            return
        }

        this.modal = modal
        this.modalTitle = title
        this.closeBtn = closeBtn
        this.lastFocusAbleElement = this._lastFocusAbleElement

        this.initControlActions()
        this.initModalActions()
    }

    /**
     * Initializes modal control actions
     */
    private initControlActions(): void {
        if (!this.modalControl) {
            return
        }
        this.modalControl.addEventListener(EventType.CLICK, () => {
            this.openModal()
        })
        this.modalControl.addEventListener(
            EventType.KEYDOWN,
            (e: KeyboardEvent) => {
                if (e.key == KeyType.ENTER) {
                    e.preventDefault()
                    this.openModal()
                }
                if (e.shiftKey && e.key === KeyType.TAB) {
                    if (ModalComponent.activated) {
                        e.preventDefault()
                        if (this.lastFocusAbleElement) {
                            this.lastFocusAbleElement.focus()
                        }
                    }
                }
            }
        )
    }

    /**
     * Initializes modal actions
     */
    private initModalActions(): void {
        this.backdropEnabled =
            (this.modal &&
                this.modal.hasAttribute(
                    ATTR_NAME.main.modal.modalUseBackdrop
                )) ||
            false

        // * attach events to custom buttons, first, last focusable element, escape key
        // close button exit
        this.closeBtn.addEventListener(EventType.CLICK, () => this.closeModal())
        this.closeBtn.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) =>
            this.handleCloseBtnKeyboard(e)
        )

        const additionalCloseBtns = this.getElements(
            `[${ATTR_NAME.main.modal.modalClose}]`,
            this.modal
        )

        if (additionalCloseBtns.length) {
            additionalCloseBtns.forEach((closeAction) => {
                closeAction.addEventListener(EventType.CLICK, () =>
                    this.closeModal()
                )
                closeAction.addEventListener(
                    EventType.KEYDOWN,
                    (e: KeyboardEvent) => {
                        if (e.key == KeyType.ENTER) {
                            e.preventDefault()
                            this.closeModal()
                        }
                    }
                )
            })
        }

        // focus trap an open modal
        if (this.lastFocusAbleElement) {
            this.lastFocusAbleElement.addEventListener(
                EventType.KEYDOWN,
                (e: KeyboardEvent) => this.handleLastElTab(e)
            )
        }

        // escape key exit modal
        document.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) =>
            this.handleEscapeKey(e)
        )
    }

    /**
     * handle the DOM updates to make a modal open
     * classes for style and animation
     * tabindex for a11y
     * aria-expanded for a11y
     * move focus out of modal for a11y
     * close the backdrop if enabled
     */
    public closeModal(): void {
        window.removeEventListener('resize', this.appHeight)
        const doc = document.documentElement
        doc.style.setProperty('--app-height', '100%')

        if (this.modal) {
            this.modal.classList.add(CLASS_NAME.modal.closed)
            this.modal.classList.remove(CLASS_NAME.modal.open)
            this.modal.tabIndex = -1
        }
        document.documentElement.classList.remove(CLASS_NAME.modal.activated)
        document.body.removeAttribute(HtmlAttr.TAB_INDEX)
        this.toggleBackdrop(BackdropStatus.CLOSE)
        window.scrollTo(0, this.lastYposition)
        if (this.modalControl) {
            this.modalControl.setAttribute(AriaAttr.EXPANDED, 'false')
            this.modalControl.focus()
        }

        ModalComponent.activated = undefined
    }

    /**
     * handle the DOM updates to make a modal open
     * classes for style and animation
     * tabindex for a11y
     * aria-expanded for a11y
     * move focus to title for a11y
     * open the backdrop if enabled
     * if first time opening the modal save the control button to the component
     */
    public openModal(): void {
        if (isMobile()) {
            window.addEventListener('resize', this.appHeight)
            this.appHeight()
        }
        // persist the user's scroll position when applying fixed screen
        this.lastYposition = window.pageYOffset
        if (this.modal) {
            this.modal.classList.add(CLASS_NAME.modal.open)
            this.modal.classList.remove(CLASS_NAME.modal.closed)
            this.modal.removeAttribute(HtmlAttr.TAB_INDEX)
            // Use setTimeout to break the JS execution thread and ensure the DOM has updated
            setTimeout(() => {
                this.loadThumbnails()
            }, 0)
        }
        document.documentElement.classList.add(CLASS_NAME.modal.activated)
        document.body.tabIndex = -1
        // add timeout for modal use tied to API response
        window.setTimeout(() => {
            if (this.modalTitle) {
                this.modalTitle.focus()
            }
            // eslint-disable-next-line no-magic-numbers
        }, 60)
        this.toggleBackdrop(BackdropStatus.OPEN)
        if (this.modalControl) {
            this.modalControl.setAttribute(AriaAttr.EXPANDED, 'true')
        }

        ModalComponent.activated = this
    }

    /**
     *  Replace src and srcset on user interaction only
     */
    private loadThumbnails(): void {
        const images = this.modal.querySelectorAll(
            '[data-src-action]'
        ) as NodeListOf<HTMLImageElement>

        // Exit early if data-src-action was already removed
        if (images.length === 0) return

        images.forEach((img) => {
            const newSrc = img.getAttribute('data-src-action')
            if (newSrc) {
                img.src = newSrc
                img.removeAttribute('data-src-action')
            }
            // Also check and update srcset from data-srcset-action if it exists
            const newSrcset = img.getAttribute('data-srcset-action')
            if (newSrcset) {
                img.srcset = newSrcset
                img.removeAttribute('data-srcset-action')
            }
        })
    }

    /**
     *  when hitting escape close the modal
     *  @param {KeyboardEvent} e
     */
    private handleEscapeKey(e: KeyboardEvent): void {
        if (ModalComponent.activated !== this) {
            return
        }

        switch (e.key) {
            case KeyType.ESC:
            case KeyType.ESC_IE:
                this.closeModal()
                break
        }
    }

    /**
     * Close button is the first focusable element and exit method
     * handle keyboard events
     * when tabbing reverse on the first element, return focus to last element
     *  @param {KeyboardEvent} e
     */
    private handleCloseBtnKeyboard(e: KeyboardEvent): void {
        if (ModalComponent.activated !== this) {
            return
        }

        if (e.key == KeyType.ENTER) {
            e.preventDefault()
            this.closeModal()
            return
        } else if (
            e.key === KeyType.TAB &&
            e.shiftKey &&
            this.lastFocusAbleElement
        ) {
            // with shift == reverse
            e.preventDefault()
            this.lastFocusAbleElement.focus()
            return
        }
    }

    /**
     * when tabbing forward on the last element, return focus to first element
     *  @param {KeyboardEvent} e
     */
    private handleLastElTab(e: KeyboardEvent): void {
        if (ModalComponent.activated !== this) {
            return
        }

        if (e.key === KeyType.TAB && !e.shiftKey && this.closeBtn) {
            e.preventDefault()
            this.closeBtn.focus()
        }
    }

    /**
     * Toggle hidden attribute on or off depending on status
     * @param {status} string enum open or close, optional
     */
    private toggleBackdrop(status: BackdropStatus): void {
        if (!this.backdropEnabled) {
            return
        }

        if (status == BackdropStatus.OPEN) {
            document.documentElement.classList.add(CLASS_NAME.modal.backdrop)
        } else {
            document.documentElement.classList.remove(CLASS_NAME.modal.backdrop)
        }
    }
}
